import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { apiGetUserProfile } from '../../../api/userCardApi';
import type { UserProfile, UserProfileOwnedCard } from '../../../api/userCardApi';
import CardDetailModal from '../../Cards/CardDetailModal';
import type { Card } from '../../../types/models/card';

const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';

const toImgSrc = (path: string | null | undefined) =>
  path ? (path.startsWith('http') ? path : storageUrl + path) : null;

const RARITY_COLOR: Record<string, string> = {
  common: 'rgba(255,255,255,0.55)',
  rare: '#4a9eff',
  epic: '#c27ff5',
  legendary: 'gold',
};

// ─── Styled ────────────────────────────────────────────────────────────────────

const Container = styled.div`
  padding: 20px 14px 70px;
  min-height: calc(100vh - 70px);
`;

const Username = styled.h1`
  font-size: 22px;
  font-weight: 900;
  color: #fff;
  margin: 0 0 2px;
`;

const Nickname = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0 0 20px;
`;

const SectionTitle = styled.h2`
  font-size: 12px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 12px;
`;

// ─── Stats ─────────────────────────────────────────────────────────────────────

const StatsRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

const StatChip = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  background-color: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 10px 12px;
`;

const StatLabel = styled.span`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatValue = styled.span`
  font-size: 18px;
  font-weight: 900;
  color: #fff;
`;

const StatValueGold = styled(StatValue)`
  color: gold;
`;

// ─── Wrestlers ─────────────────────────────────────────────────────────────────

const WrestlersSection = styled.div`
  margin-bottom: 24px;
`;

const WrestlersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WrestlerCard = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  color: #fff;
  width: 100%;
  text-align: left;
  transition: border-color 0.15s;

  &:active {
    border-color: rgba(255, 215, 0, 0.4);
  }
`;

const WrestlerAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.08);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
  }
`;

const WrestlerName = styled.span`
  font-size: 14px;
  font-weight: 600;
`;

// ─── Cards grid ────────────────────────────────────────────────────────────────

const CardsSection = styled.div``;

const CardsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const CardsCount = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background-color: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 14px;
  &:focus { outline: none; border-color: gold; }
  &::placeholder { color: rgba(255,255,255,0.3); }
`;

const ExpansionSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background-color: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 14px;
  &:focus { outline: none; border-color: gold; }
  option { background-color: #1a1a1a; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`;

const CardItem = styled.div`
  cursor: pointer;
  border-radius: 5px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background-color: #222;
`;

const CardImgWrapper = styled.div`
  width: 100%;
  aspect-ratio: 2 / 3;
  background-color: #2a2a2a;
  overflow: hidden;
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardImgPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.15);
  font-size: 10px;
`;

const CardFooter = styled.div`
  padding: 4px 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #1a1a1a;
`;

const CardIdLabel = styled.span`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.3);
`;

const RarityDot = styled.span<{ $color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const QtyBadge = styled.span`
  font-size: 9px;
  color: rgba(255, 255, 255, 0.4);
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  padding: 1px 3px;
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 32px;
  font-size: 14px;
`;

// ─── Component ─────────────────────────────────────────────────────────────────

const UserProfileMobile: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<UserProfileOwnedCard | null>(null);
  const [search, setSearch] = useState('');
  const [expansionFilter, setExpansionFilter] = useState<number | 'all'>('all');

  useEffect(() => {
    if (!username) return;
    setIsLoading(true);
    setNotFound(false);
    apiGetUserProfile(username)
      .then((data) => setProfile(data))
      .catch(() => setNotFound(true))
      .finally(() => setIsLoading(false));
  }, [username]);

  const validCards = useMemo(
    () => (profile?.owned_cards ?? []).filter((e) => e.card !== null),
    [profile]
  );

  const expansionOptions = useMemo(() => {
    const map = new Map<number, string>();
    validCards.forEach((e) => e.card!.expansions.forEach((exp) => map.set(exp.id, exp.name)));
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [validCards]);

  const filtered = useMemo(() => {
    return validCards.filter((entry) => {
      const card = entry.card!;
      if (expansionFilter !== 'all' && !card.expansions.some((e) => e.id === expansionFilter)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return card.wrestlers.some((w) => w.name.toLowerCase().includes(q));
      }
      return true;
    });
  }, [validCards, expansionFilter, search]);

  if (isLoading) return <Container><StatusText>Caricamento...</StatusText></Container>;
  if (notFound || !profile) return <Container><StatusText>Utente non trovato.</StatusText></Container>;

  const toCard = (entry: UserProfileOwnedCard): Card | null => {
    if (!entry.card) return null;
    return {
      id: entry.card.id,
      rarity: entry.card.rarity,
      fronte_path: entry.card.fronte_path,
      retro_path: null,
      notes: null,
      author_user_id: entry.card.author_user_id,
      author_name: entry.card.author_name,
      author_user: entry.card.author_user,
      wrestlers: entry.card.wrestlers.map((w) => ({ ...w, avatar_path: w.avatar_path ?? '' })),
      ppvs: [],
      belts: [],
      tags: [],
      expansions: entry.card.expansions.map((e) => ({ ...e, expansion_path: null, pivot: { drop_rate: 0 } })),
      price: null,
      shop_disabled: false,
      reference_url: entry.card.reference_url,
      reference_string: entry.card.reference_string,
      reference_data: entry.card.reference_data ?? null,
    };
  };

  const selectedCard = selectedEntry ? toCard(selectedEntry) : null;

  return (
    <>
      <Container>
        <Username>{profile.username}</Username>
        {profile.nickname_forum && <Nickname>{profile.nickname_forum}</Nickname>}

        {/* Stats */}
        <StatsRow>
          <StatChip>
            <StatLabel>Carte</StatLabel>
            <StatValue>{profile.stats.unique_cards}</StatValue>
          </StatChip>
          <StatChip>
            <StatLabel>Copie</StatLabel>
            <StatValue>{profile.stats.total_copies}</StatValue>
          </StatChip>
          <StatChip>
            <StatLabel>Leg.</StatLabel>
            <StatValueGold>{profile.stats.legendary_count}</StatValueGold>
          </StatChip>
        </StatsRow>

        {/* Wrestlers */}
        {profile.wrestlers.length > 0 && (
          <WrestlersSection>
            <SectionTitle>Wrestler assegnati</SectionTitle>
            <WrestlersList>
              {profile.wrestlers.map((w) => {
                const avatarSrc = toImgSrc(w.avatar_path);
                return (
                  <WrestlerCard key={w.id} onClick={() => navigate(`/wrestlers/i_${w.id}_d_${w.name}`)}>
                    <WrestlerAvatar>
                      {avatarSrc && <img src={avatarSrc} alt={w.name} />}
                    </WrestlerAvatar>
                    <WrestlerName>{w.name}</WrestlerName>
                  </WrestlerCard>
                );
              })}
            </WrestlersList>
          </WrestlersSection>
        )}

        {/* Carte */}
        <CardsSection>
          <CardsHeader>
            <SectionTitle style={{ margin: 0 }}>Carte possedute</SectionTitle>
            <CardsCount>{filtered.length} / {validCards.length}</CardsCount>
          </CardsHeader>

          <FiltersRow>
            <SearchInput
              type="text"
              placeholder="Cerca per wrestler..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FiltersRow>

          {expansionOptions.length > 0 && (
            <ExpansionSelect
              value={expansionFilter}
              onChange={(e) => setExpansionFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              style={{ marginBottom: 12 }}
            >
              <option value="all">Tutte le espansioni</option>
              {expansionOptions.map((exp) => (
                <option key={exp.id} value={exp.id}>{exp.name}</option>
              ))}
            </ExpansionSelect>
          )}

          {validCards.length === 0 && (
            <StatusText style={{ marginTop: 16 }}>Nessuna carta ancora.</StatusText>
          )}

          {validCards.length > 0 && filtered.length === 0 && (
            <StatusText style={{ marginTop: 16 }}>Nessuna carta corrisponde ai filtri.</StatusText>
          )}

          <Grid>
            {filtered.map((entry) => {
              const card = entry.card!;
              const src = toImgSrc(card.fronte_path);
              return (
                <CardItem key={entry.card_id} onClick={() => setSelectedEntry(entry)}>
                  <CardImgWrapper>
                    {src
                      ? <CardImg src={src} alt={`Carta #${card.id}`} />
                      : <CardImgPlaceholder>#{card.id}</CardImgPlaceholder>
                    }
                  </CardImgWrapper>
                  <CardFooter>
                    <CardIdLabel>#{card.id}</CardIdLabel>
                    {entry.quantity > 1 && <QtyBadge>x{entry.quantity}</QtyBadge>}
                    <RarityDot $color={RARITY_COLOR[card.rarity] ?? 'rgba(255,255,255,0.3)'} />
                  </CardFooter>
                </CardItem>
              );
            })}
          </Grid>
        </CardsSection>
      </Container>

      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          ownedQuantity={selectedEntry?.quantity ?? 0}
          isAuthenticated={false}
          onClose={() => setSelectedEntry(null)}
        />
      )}
    </>
  );
};

export default UserProfileMobile;
